import React, { useState, useEffect } from 'react';
import "./HistoricoCompra.css";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button,
  TextField,
  MenuItem,
  Grid,
  Divider,
  Typography,
  Pagination,
  Alert,
  Box
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import dayjs from 'dayjs';
import ModalItens from './ModalItens';
import ModalEnviarNota from './ModalEnviarNota';
import api from '../../../services/api';
import API_CONFIG from '../../../config/api.config';

const HistoricoCompra = () => {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Estados para os filtros
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [prefeituraSelecionada, setPrefeituraSelecionada] = useState('');
  const [comprasFiltradas, setComprasFiltradas] = useState([]);
  const [notasEnviadas, setNotasEnviadas] = useState(new Set());

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Estados para os modais
  const [modalItensOpen, setModalItensOpen] = useState(false);
  const [modalNotaOpen, setModalNotaOpen] = useState(false);
  const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState(null);

  // Lista de prefeituras únicas para o filtro
  const [prefeituras, setPrefeituras] = useState([]);

  // Monitora mudanças no menu
  useEffect(() => {
    const handleMenuChange = (event) => {
      setIsMenuExpanded(!event.detail.isCollapsed);
    };

    window.addEventListener('menuStateChange', handleMenuChange);
    return () => window.removeEventListener('menuStateChange', handleMenuChange);
  }, []);

  // Buscar dados da API
  const buscarCompras = async (page = 1) => {
    try {
      setLoading(true);
      setError('');

      const idFornecedor = localStorage.getItem('idFornecedor');
      if (!idFornecedor) {
        setError('ID do fornecedor não encontrado.');
        return;
      }

      const params = {
        page,
        FOR_ID: idFornecedor
      };

      // Adiciona filtros de data apenas se estiverem preenchidos
      if (dataInicio) {
        params.data_inicio = dataInicio;
      }
      if (dataFim) {
        params.data_fim = dataFim;
      }

      const response = await api.get(API_CONFIG.ENDPOINTS.COMPRAS.LISTAR_HISTORICO_COMPRAS_FORNECEDOR, {
        params
      });

      if (response.data.status && response.data.vendas) {
        const vendas = response.data.vendas;
        setCompras(vendas.data || []);
        setTotalPages(vendas.last_page || 1);
        setTotalItems(vendas.total || 0);
        setCurrentPage(vendas.current_page || 1);

        // Extrair prefeituras únicas dos dados
        const prefeiturasUnicas = [...new Set(
          vendas.data.map(compra => 
            compra.cotacao?.solicitacao?.prefeitura?.PRE_NOME || 'Prefeitura não informada'
          )
        )];
        setPrefeituras(prefeiturasUnicas);

        // Aplicar filtro de prefeitura se selecionado
        aplicarFiltroPrefeitura(vendas.data, prefeituraSelecionada);
      } else {
        setCompras([]);
        setComprasFiltradas([]);
        setPrefeituras([]);
      }
    } catch (error) {
     // console.error('Erro ao buscar compras:', error);
      setError('Erro ao carregar histórico de vendas. Tente novamente.');
      setCompras([]);
      setComprasFiltradas([]);
    } finally {
      setLoading(false);
    }
  };

  // Função para aplicar filtro de prefeitura localmente
  const aplicarFiltroPrefeitura = (listaCompras, prefeitura) => {
    if (!prefeitura) {
      setComprasFiltradas(listaCompras);
    } else {
      const filtradas = listaCompras.filter(compra => {
        const nomePrefeitura = compra.cotacao?.solicitacao?.prefeitura?.PRE_NOME;
        return nomePrefeitura && nomePrefeitura.toLowerCase().includes(prefeitura.toLowerCase());
      });
      setComprasFiltradas(filtradas);
    }
  };

  // Aplica os filtros
  useEffect(() => {
    aplicarFiltroPrefeitura(compras, prefeituraSelecionada);
  }, [compras, prefeituraSelecionada]);

  // Buscar dados iniciais
  useEffect(() => {
    buscarCompras();
  }, []);

  // Buscar dados quando mudar página
  useEffect(() => {
    buscarCompras(currentPage);
  }, [currentPage, dataInicio, dataFim]);

  const formatarValor = (valor) => {
    if (!valor) return 'R$ 0,00';
    return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatarData = (dataString) => {
    if (!dataString) return '---';
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR');
  };

  const calcularValorTotal = (itens) => {
    if (!itens || itens.length === 0) return 0;
    return itens.reduce((total, item) => {
      const valor = Number(item.item_compra?.ITC_VALOR_REVISADO || item.item_compra?.ITC_VALOR_ORIGINAL || 0);
      const quantidade = Number(item.IT_QUANTIDADE || 0);
      return total + (valor * quantidade);
    }, 0);
  };

  const handleVerItens = (solicitacao) => {
    setSolicitacaoSelecionada(solicitacao);
    setModalItensOpen(true);
  };

  const handleEnviarNota = (solicitacao) => {
    setSolicitacaoSelecionada(solicitacao);
    setModalNotaOpen(true);
  };

  const handleNotaEnviada = (compraId) => {
    setNotasEnviadas(prev => new Set([...prev, compraId]));
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleFiltrar = () => {
    setCurrentPage(1);
    buscarCompras(1);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={`historico-compra-for-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
      <div className="historico-compra-for-wrapper">
        <h1 className="historico-compra-for-title">Histórico de Vendas para Prefeituras</h1>
        <div className="historico-compra-for-content">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid
            container
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 2 }}
          >
            <Grid item xs={12} sm="auto">
              <TextField
                label="Data Início"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <TextField
                label="Data Fim"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <TextField
                select
                label="Prefeitura"
                fullWidth
                size="small"
                value={prefeituraSelecionada}
                onChange={(e) => setPrefeituraSelecionada(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {prefeituras.map((prefeitura, index) => (
                  <MenuItem key={index} value={prefeitura}>
                    {prefeitura}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="contained"
                startIcon={<FilterAltIcon />}
                onClick={handleFiltrar}
                className="historico-compra-for-filter-button"
                disabled={loading}
                fullWidth
                style={{ minWidth: 220, maxWidth: 300, height: 40 }}
              >
                {loading ? 'Carregando...' : 'Filtrar'}
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {isMobile ? (
            <div>
              {comprasFiltradas.length > 0 ? (
                comprasFiltradas.map((compra) => {
                  const valorTotal = calcularValorTotal(compra.itens);
                  const prefeituraNome = compra.cotacao?.solicitacao?.prefeitura?.PRE_NOME || 'Prefeitura não informada';
                  const notaEnviada = compra.COM_NOTA_ENVIADA === 'SIM';
                  
                  return (
                    <Paper key={compra.COM_ID} className="historico-compra-for-card" elevation={2} sx={{ mb: 2, p: 2 }}>
                      <div><b>Nº Solicitação:</b> {compra.COM_SOL_ID}</div>
                      <div><b>Prefeitura:</b> {prefeituraNome}</div>
                      <div><b>Data:</b> {formatarData(compra.COM_DATA_VINCULACAO)}</div>
                      <div><b>Valor:</b> {formatarValor(valorTotal)}</div>
                      <div><b>Status:</b> {compra.cotacao?.COT_STATUS || '---'}
                        {notaEnviada && (
                          <Typography 
                            variant="caption" 
                            display="block" 
                            sx={{ color: 'success.main', fontWeight: 'bold' }}
                          >
                            Nota Enviada
                          </Typography>
                        )}
                      </div>
                      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          onClick={() => handleEnviarNota(compra)}
                          sx={{ minWidth: '120px' }}
                          disabled={notaEnviada}
                        >
                          {notaEnviada ? 'Nota Enviada' : 'Enviar Nota'}
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color="secondary"
                          onClick={() => handleVerItens(compra)}
                          sx={{ minWidth: '120px' }}
                        >
                          Ver Itens
                        </Button>
                      </div>
                    </Paper>
                  );
                })
              ) : (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  {loading ? 'Carregando...' : 'Nenhuma venda encontrada com os filtros aplicados'}
                </Paper>
              )}
            </div>
          ) : (
            <TableContainer component={Paper} className="historico-compra-for-table-container">
              <Table stickyHeader size="small" className="historico-compra-for-table">
                <TableHead>
                  <TableRow className="historico-compra-for-table-header">
                    <TableCell>Nº Solicitação</TableCell>
                    <TableCell>Prefeitura</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comprasFiltradas.length > 0 ? (
                    comprasFiltradas.map((compra) => {
                      const valorTotal = calcularValorTotal(compra.itens);
                      const prefeituraNome = compra.cotacao?.solicitacao?.prefeitura?.PRE_NOME || 'Prefeitura não informada';
                      const notaEnviada = compra.COM_NOTA_ENVIADA === 'SIM';
                      
                      return (
                        <TableRow key={compra.COM_ID} hover>
                          <TableCell>{compra.COM_SOL_ID}</TableCell>
                          <TableCell>{prefeituraNome}</TableCell>
                          <TableCell>{formatarData(compra.COM_DATA_VINCULACAO)}</TableCell>
                          <TableCell align="right">{formatarValor(valorTotal)}</TableCell>
                          <TableCell>
                            {compra.cotacao?.COT_STATUS || '---'}
                            {notaEnviada && (
                              <Typography 
                                variant="caption" 
                                display="block" 
                                sx={{ color: 'success.main', fontWeight: 'bold' }}
                              >
                                Nota Enviada
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => handleEnviarNota(compra)}
                              sx={{ mr: 1, minWidth: '120px' }}
                              disabled={notaEnviada}
                            >
                              {notaEnviada ? 'Nota Enviada' : 'Enviar Nota'}
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="secondary"
                              onClick={() => handleVerItens(compra)}
                              sx={{ minWidth: '120px' }}
                            >
                              Ver Itens
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" className="historico-compra-for-no-results">
                        {loading ? 'Carregando...' : 'Nenhuma venda encontrada com os filtros aplicados'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Informações de paginação */}
          {totalItems > 0 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Mostrando {comprasFiltradas.length} de {totalItems} vendas
            </Typography>
          )}
        </div>
      </div>

      {/* Modal de Itens */}
      <ModalItens
        open={modalItensOpen}
        onClose={() => setModalItensOpen(false)}
        solicitacao={solicitacaoSelecionada}
      />

      {/* Modal de Enviar Nota */}
      <ModalEnviarNota
        open={modalNotaOpen}
        onClose={() => setModalNotaOpen(false)}
        solicitacao={solicitacaoSelecionada}
        onNotaEnviada={handleNotaEnviada}
      />
    </div>
  );
};

export default HistoricoCompra;